import {useEffect, useRef} from 'react'
import {CSSObject} from '@emotion/react'
import * as typeformEmbed from '@typeform/embed'
import {Helmet} from 'react-helmet-async'
import styled from '@emotion/styled'
import {useLocation} from '@reach/router'

import * as styles from 'src/styles'
import {Theme} from 'src/styles/theme'
import * as breakpoints from 'src/styles/mediaQuery'
import {FlairSampler, useFlairContext} from 'src/context/FlairContext'
import useViewer from 'src/hooks/useViewer'

import {isLmsImplementation} from '../lib/util'

interface SectionProps {
  bundle?: FlairSampler
}
type WysiwygStyleFn = (props: {theme: Theme} & SectionProps) => CSSObject

const coreArticleCss: WysiwygStyleFn = ({theme}) => ({
  h1: {
    // h1 tags shouldn't be used by content writers so they're downstyled here
    // to match body text and encourage desired behavior.
    ...styles.typography.body,
    ...styles.typography.breakWord,
    marginBottom: theme.space[3],
  },
  h2: {
    ...styles.typography.header2,
    ...styles.typography.breakWord,
    marginTop: theme.space[7],
    marginBottom: theme.space[3],
  },
  h3: {
    ...styles.typography.header3,
    ...styles.typography.breakWord,
    marginTop: theme.space[6],
    marginBottom: theme.space[3],
  },
  'h4, h5, h6': {
    // h4, h5, and h6 tags shouldn't be used by content writers. They're
    // upstyled here to match the h3 tag should they find their way into article
    // content.
    ...styles.typography.header3,
    ...styles.typography.breakWord,
    marginTop: theme.space[7],
    marginBottom: theme.space[3],
  },

  p: {
    marginBottom: theme.space[3],
  },
  '*:last-child': {
    marginBottom: 0,
  },
  'strong, b': {
    fontWeight: theme.fontWeights.semiBold,
  },
  'u, i, em': {
    fontWeight: 'inherit',
  },
  'i, em': {
    fontStyle: 'italic',
  },
  'code, tt, kbd': {
    fontFamily: 'monospace',
  },
  a: {
    color: theme.colors.link,

    '&:visited': {
      color: theme.colors.visitedLink,
    },
  },

  table: {
    tableLayout: 'fixed',
    width: '100%',
  },

  'td, th': {
    wordWrap: 'break-word',
    hyphens: 'auto',
  },

  blockquote: {
    backgroundColor: theme.colors.grey1,
    margin: `${theme.space[4]} ${theme.space[3]}`,
    padding: theme.space[3],

    [breakpoints.medium]: {
      margin: theme.space[4],
      padding: theme.space[4],
    },
  },

  li: {
    wordWrap: 'break-word',
  },

  '> ol, > ul': {
    marginBottom: theme.space[3],
  },
  '> ul , > ul ul, > ul ul ul': {
    paddingLeft: theme.space[3],
  },
  '> ol , > ol ol, > ol ol ol': {
    paddingLeft: theme.space[5],
  },
  '> ul > li': {
    listStyleType: 'disc',
  },
  '> ul ul > li': {
    listStyleType: 'circle',
  },
  '> ul ul ul > li': {
    listStyleType: 'square',
  },
  '> ol > li': {
    listStyleType: 'decimal',
  },
  '> ol ol > li': {
    listStyleType: 'lower-alpha',
  },
  '> ol ol ol > li': {
    listStyleType: 'lower-roman',
  },
  'li + li, li > ul, li > ol': {
    marginTop: theme.space[1],
  },

  'img, iframe': {
    display: 'block',
    maxWidth: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: theme.space[4],
    marginTop: theme.space[4],
  },

  'p > img' : {
    '@media (max-width: 767px)': {
      width: '100%',
      height: 'auto',
    },
  },
})

const embeddedTwitterCss: WysiwygStyleFn = ({theme}) => ({
  'twitter-widget': {
    marginLeft: 'auto',
    marginRight: 'auto',
    marginBottom: `${theme.space[4]} !important`,
    marginTop: `${theme.space[4]} !important`,
  },
})

const embededInstagramCss: WysiwygStyleFn = ({theme}) => ({
  'blockquote[data-instgrm-permalink], iframe.instagram-media': {
    marginLeft: 'auto !important',
    marginRight: 'auto !important',
    marginBottom: `${theme.space[4]} !important`,
    marginTop: `${theme.space[4]} !important`,
  },
})

const backgroundContent = (url: string) => ({
  '&:before': {
    backgroundImage: `url(${url})`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
  },
})

const headerFlair: WysiwygStyleFn = ({bundle}) => {
  if (!bundle) {
    return {}
  }

  const firstFlair = bundle.flair('first')
  const secondFlair = bundle.flair('second')
  const thirdFlair = bundle.flair('third')

  return {
    h2: {
      position: 'relative',
      '&:before': {
        position: 'absolute',
        content: "''",
        padding: 42.5,
        left: -20,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: -1,
      },
    },
    'h2:nth-of-type(3n+1)': firstFlair && backgroundContent(firstFlair),
    'h2:nth-of-type(3n+2)': secondFlair && backgroundContent(secondFlair),
    'h2:nth-of-type(3n+3)': thirdFlair && backgroundContent(thirdFlair),
  }
}

const WysiwygContentSection = styled.section<SectionProps>([
  coreArticleCss,
  embeddedTwitterCss,
  embededInstagramCss,
  headerFlair,
])

const appendValuesToUrl = (baseUrl: string, data: {} = {}) => {
  const fitleredEntries = Object.entries(data).filter(
    // Remove undefined and null values but allow explicit false.
    ([_, value]) => typeof value !== 'undefined' && value !== null
  )

  // Escape keys and values and create an array of strings, eg. "foo=bar"
  const params = []
  for (const [key, value] of fitleredEntries) {
    const escKey = encodeURI(key)
    const escValue = encodeURI(`${value}`)
    params.push(`${escKey}=${escValue}`)
  }
  const paramsStr = params.join('&')

  const url = paramsStr ? `${baseUrl}?${paramsStr}` : baseUrl
  return url
}

interface Props {
  content: string
  withFlair?: boolean
}

const WysiwygContent = ({content, withFlair = true}: Props) => {
  const bundle = useFlairContext()
  const {viewer} = useViewer()

  // Create and display Typeform widgets. This method solves two problems:
  // 1) Typeform embeds were not being displayed again when after a user was
  //    returned to the page after logging in or by pressing the back button in
  //    their browser (component rerendered but embed script would not execute
  //    again).
  // 2) We needed a way to pass the user's ID to Typeform without
  //    parsing/replacing the content coming from the CMS.
  useEffect(() => {
    // Find all Typeform embeds on the page.
    const typeformEmbedNodes =
      document.getElementsByClassName('typeform-widget')
    console.log('nodes', typeformEmbedNodes)

    for (const node of typeformEmbedNodes) {
      // data-url should always exist, but skip this node if it doesn't
      // (primarily to make TypeScript happy).
      const dataUrl = node.getAttribute('data-url')
      if (!dataUrl) {
        continue
      }

      // Pass the user ID to Typeform if it exists.
      const embedUrl = appendValuesToUrl(dataUrl, {
        user_id: viewer !== 'not-logged-in' && viewer?.student.id,
      })
      //arbitrary change comment
      typeformEmbed.makeWidget(node, embedUrl)
    }
  })

  // Similar to Typeform effect above, but less complicated: rerender Instagram
  // embeds when the component is rerendered.
  useEffect(() => {
    // eslint-disable-next-line no-unused-expressions
    ;(window as any).instgrm?.Embeds.process()
  })

  // Parse the content and if this in an LMS implementation, add the query to all internal links so they persist as LMS implementations
  const currentLocation = useLocation()
  const wContent = useRef(content)

  useEffect(() => {
    const isLMS = isLmsImplementation(currentLocation)

    if (isLMS) {
      const container = document.createElement('div')
      container.innerHTML = content
      const links = container.getElementsByTagName('a')

      for (let i = 0; i < links.length; i++) {
        if (
          links[i].search.includes('lms=true') ||
          links[i].origin !== currentLocation.origin
        ) {
          continue
        }
        if (links[i].search) {
          links[i].search += '&lms=true'
        } else {
          links[i].search = '?lms=true'
        }
      }

      if (container.firstElementChild?.outerHTML) {
        wContent.current = container.innerHTML
      }
    }
  }, [content, currentLocation])

  return (
    <>
      <Helmet>
        <script
          async
          defer
          src="//platform.instagram.com/en_US/embeds.js"
        ></script>
      </Helmet>

      <WysiwygContentSection
        bundle={withFlair ? bundle : undefined}
        dangerouslySetInnerHTML={{__html: wContent.current}}
      />
    </>
  )
}

export default WysiwygContent
